import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProjectById, updateProject } from "../api/project";
import SlidesBar from "../components/SlidesBar/SlidesBar";
import SlideSettingsBar from "../components/SlideSettingsBar/SlideSettingsBar";
import SlideCanvas from "../components/SlideCanvas/SlideCanvas";
import { useUIContext } from "../contexts/UIContext";
import { useWorkspaceContext } from "../contexts/WorkspaceContext";
import { useSlideContext } from "../contexts/SlideContext";
import Button from "../components/common/Button";
import UserDisplay from "../components/UserDisplay/UserDisplay";
import Icon from "../components/common/Icon";
import { toast } from "react-toastify";

const ProjectEdit = () => {
  const { projectUuid } = useParams();
  const {
    projectData,
    setProjectData,
    setProjectPermissions,
    setSlides,
    selectedSlide,
    setSelectedSlide,
    projectPermissions,
    autoSave,
  } = useSlideContext();
  const { setSelectedWorkspace, ownedWorkspaces, memberWorkspaces } =
    useWorkspaceContext();
  const navigate = useNavigate();
  const { isSaving, setIsSaving, setIsSaveError } = useUIContext();

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectUuid) return;

      try {
        const project = await getProjectById(projectUuid);

        const formattedProjectData = {
          projectUuid: project.uuid,
          title: project.title,
          type: project.type,
          isPublished: project.isPublished,
          workspaceId: project.workspace.id,
          workspaceName: project.workspace.name,
          updatedAt: project.updatedAt,
          data: { slides: project.data.slides },
        };

        setProjectData(formattedProjectData);

        if (project.permissions) {
          setProjectPermissions(project.permissions);
        }

        const foundWorkspace =
          ownedWorkspaces.find(
            (workspace) => workspace.workspaceId === project.workspace.id
          ) ||
          memberWorkspaces.find(
            (workspace) => workspace.workspaceId === project.workspace.id
          );

        if (foundWorkspace) {
          setSelectedWorkspace(foundWorkspace);
        }
      } catch (error: any) {
        if (error.message === "401") {
          console.error("Oturum açılmamış veya süresi geçmiş:", error);
          navigate("/login-required"); // Giriş yapılması gerekiyor sayfasına yönlendir
        } else if (error.message === "403") {
          console.error("Yetkisiz erişim:", error);
          navigate("/forbidden"); // Forbidden sayfasına yönlendir
        } else if (error.message === "404") {
          console.error("Proje bulunamadı:", error);
          navigate("/not-found"); // Not Found sayfasına yönlendir
        } else {
          console.error("Bir hata oluştu:", error);
          navigate("/error"); // Genel hata sayfasına yönlendir
        }
      }
    };

    fetchProjectData();
  }, [projectUuid]);

  useEffect(() => {
    if (projectData?.data?.slides) {
      setSlides(projectData.data.slides);
        
      // Eğer seçili slide yoksa veya mevcut slide artık slides içinde değilse
      if (
        !selectedSlide ||
        !projectData.data.slides.some((slide) => slide.id === selectedSlide.id)
      ) {
        setSelectedSlide(projectData.data.slides[0] || null); // İlk slide'a git
      } else {
        // Seçili slide varsa ve hala slides içindeyse, güncel versiyonunu set et
        const updatedSelectedSlide = projectData.data.slides.find(
          (slide) => slide.id === selectedSlide.id
        );
        setSelectedSlide(updatedSelectedSlide || null);
      }
    }
  }, [projectData]);

  // Autosave Mechanism
  useEffect(() => {
    if (
      !projectData ||
      !projectUuid ||
      !projectPermissions.includes("edit") ||
      !autoSave
    ) {
      return; // Kullanıcıda "edit" yetkisi yoksa kaydetme işlemini yapma
    }

    setIsSaving(true);
    setIsSaveError(false);

    const timeoutId = setTimeout(async () => {
      try {
        await updateProject(projectUuid, projectData);
        setIsSaving(false);
      } catch (error) {
        setIsSaving(false);
        setIsSaveError(true);
        console.error("Proje güncellenemedi:", error);
      }
    }, 2000);

    return () => clearTimeout(timeoutId); // Önceki zamanlayıcıyı temizle
  }, [projectData, projectUuid]);

  return (
    <div className="w-screen h-screen p-6 pb-8 flex flex-col bg-white">
      <Header />
      <main className="flex justify-between gap-x-6 w-full h-[calc(100%-64px)] mt-6">
        <SlidesBar />
        <SlideCanvas />
        <SlideSettingsBar />
      </main>
    </div>
  );
};

export default ProjectEdit;

const Header = () => {
  const { projectData, projectPermissions } = useSlideContext();
  const { showModal } = useUIContext();
  const { setSelectedWorkspace, ownedWorkspaces, memberWorkspaces } =
    useWorkspaceContext();
  const navigate = useNavigate();

  const handleNavigateHome = () => {
    if (projectData) {
      const { workspaceId } = projectData;

      const foundWorkspace =
        ownedWorkspaces.find(
          (workspace) => workspace.workspaceId === workspaceId
        ) ||
        memberWorkspaces.find(
          (workspace) => workspace.workspaceId === workspaceId
        );

      if (foundWorkspace) {
        setSelectedWorkspace(foundWorkspace);
        navigate("/"); // Ana sayfaya yönlendir
      } else {
        navigate("/"); // Ana sayfaya yönlendir

      }
    } else {
      console.error("Project data mevcut değil!");
    }
  };

  if (!projectData) {
    return null;
  }

  const canEdit = projectPermissions.includes("edit");

  return (
    <header className="flex items-center justify-between h-16 w-full">
      <div className="flex items-center gap-2">
        <Icon name="workspace-icon" size={20} />
        <h3 className="flex items-center gap-2 font-medium text-sm text-[#3d3d3c]">
          <span
            className="cursor-pointer hover:underline"
            onClick={handleNavigateHome}
          >
            {projectData.workspaceName || "Workspace"}
          </span>
          {">"}
          <span>{projectData.title || "Proje"}</span>
        </h3>
      </div>
      <div className="flex items-center justify-center gap-8">
        <Button
          label="Projeyi Paylaş"
          iconName="share-icon"
          className={`bg-[#0D92F4] ${
            canEdit ? "" : "cursor-not-allowed opacity-50"
          }`}
          textClassName="text-sm text-white"
          onClick={
            canEdit ? () => showModal("isShareProjectModalVisible") : undefined
          }
        />
        <Button
          label="Yayın Ayarları"
          iconName="publish-icon"
          className={`bg-[#191919] ${
            canEdit ? "" : "cursor-not-allowed opacity-50"
          }`}
          textClassName="text-sm text-white"
          onClick={
            canEdit
              ? () => showModal("isPublishProjectModalVisible")
              : undefined
          }
        />

        <UserDisplay />
      </div>
    </header>
  );
};
